import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { useFormatAmount } from 'core/hooks/useFormatAmount'
import { useGetCurrencySymbol } from 'core/hooks/useGetCurrencySymbol'
import { StoreTrajectoryContext } from '../../state/store-trajectory-context'
import AccountCard from './account-card'
import { Instore } from '../../../../../core/api/trajectory-photo/useApiGetStoreForecast'
import { VpStoreIcon } from '@vtmn-play/icons/react'
import { useFormatNumber } from '../../../../../core/hooks/useFormatNumber'
import { VpDivider } from '@vtmn-play/react'
import { formatMarginRate } from './format-margin-rate'
import { InputType } from './input-type'

type ChannelInstoreProps = {
  instore: Instore
}

const ChannelInstore = ({ instore }: ChannelInstoreProps) => {
  const { t } = useTranslation()
  const formatNumber = useFormatNumber()
  const formatAmount = useFormatAmount()
  const getCurrencySymbol = useGetCurrencySymbol()

  const {
    store,
    referenceYear,
    forecastInstoreQuantities: forecastQuantities,
    setForecastInstoreGmv,
    isForecastInstoreGmvValid,
    setForecastInstoreMarginRate,
    isForecastInstoreMarginRateValid,
    setForecastInstoreSellingPrice,
    isForecastInstoreSellingPriceValid
  } = useContext(StoreTrajectoryContext)

  const currency = store?.currency ?? ''
  const currencySymbol = getCurrencySymbol(currency)

  const referenceQuantities = instore.reference_quantities
  const forecastQuantitiesProgression = forecastQuantities / referenceQuantities - 1

  return (
    <div className="mg-store-trajectory-edit-channels-instore">
      <div className="mg-channels-channel-title">
        <VpStoreIcon size={24} />
        <div className="mg-channels-channel-title-text vp-subtitle-l" data-testid="mg-channels-title-text">
          {t('general.trajectory.store.edit.channels.instore.title')}
        </div>
      </div>

      <AccountCard
        title={t('general.trajectory.store.edit.channels.account.gmv.title', { referenceYear })}
        referenceValue={instore.reference_gmv}
        referenceProgression={instore.reference_gmv_progression}
        referenceAiValue={instore.reference_gmv_ai}
        referenceAiProgression={instore.reference_gmv_ai_progression}
        initialForecastValue={instore.target_gmv}
        initialForecastProgression={instore.target_gmv_progression}
        valueField={{
          label: t('general.trajectory.store.edit.channels.account.gmv.description', {
            currency: currencySymbol
          }),
          type: InputType.AMOUNT,
          isValid: isForecastInstoreGmvValid
        }}
        formatValue={value =>
          formatAmount(value, currency, '--', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
        }
        onForecastChange={setForecastInstoreGmv}
      />

      <VpDivider />

      <AccountCard
        title={t('general.trajectory.store.edit.channels.account.margin_rate.title', { referenceYear })}
        referenceValue={instore.reference_margin_rate}
        referenceProgression={instore.reference_margin_rate_progression}
        initialForecastValue={instore.target_margin_rate}
        initialForecastProgression={instore.target_margin_rate_progression}
        valueField={{
          label: t('general.trajectory.store.edit.channels.account.margin_rate.description'),
          type: InputType.PERCENTAGE_PRECISE,
          isValid: isForecastInstoreMarginRateValid
        }}
        formatValue={value => formatMarginRate(formatNumber, value)}
        onForecastChange={setForecastInstoreMarginRate}
      />

      <VpDivider />

      <AccountCard
        title={t('general.trajectory.store.edit.channels.account.selling_price.title', { referenceYear })}
        referenceValue={instore.reference_selling_price}
        referenceProgression={instore.reference_selling_price_progression}
        initialForecastValue={instore.target_selling_price}
        initialForecastProgression={instore.target_selling_price_progression}
        valueField={{
          label: t('general.trajectory.store.edit.channels.account.selling_price.description', {
            currency: currencySymbol
          }),
          type: InputType.AMOUNT_PRECISE,
          isValid: isForecastInstoreSellingPriceValid
        }}
        formatValue={value =>
          formatAmount(value, currency, '--', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        }
        onForecastChange={setForecastInstoreSellingPrice}
      />

      <VpDivider />

      <AccountCard
        title={t('general.trajectory.store.edit.channels.account.quantities', { referenceYear })}
        referenceValue={forecastQuantities}
        referenceProgression={forecastQuantitiesProgression}
        formatValue={value => formatAmount(value, '', '--', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
      />
    </div>
  )
}

export default ChannelInstore
